import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { adminGetStats, adminListCourses, adminCreateCourse, adminDeleteCourse } from "@/services/adminApi";
import { BookOpen, Users, Award, Plus, Trash2, Edit3, ChevronRight, AlertCircle } from "lucide-react";

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div
      className="p-5"
      style={{
        background: color,
        borderRadius: "20px",
        border: "1.5px solid #242220",
        boxShadow: "4px 4px 0 #242220",
      }}
    >
      <Icon size={20} strokeWidth={2.5} color="#242220" />
      <p className="text-3xl font-bold mt-2" style={{ color: "#242220" }}>{value}</p>
      <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.7 }}>{label}</p>
    </div>
  );
}

function NewCourseModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ identifier: "", name: "", description: "", cover_image: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.identifier || !form.name) { setError("Az azonosító és a név kötelező."); return; }
    setLoading(true);
    try {
      const res = await adminCreateCourse(form);
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Hiba történt.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: "#FDF5EB", borderRadius: "14px", border: "1.5px solid #242220",
    fontFamily: "Quicksand, sans-serif", color: "#242220", width: "100%",
    padding: "10px 14px", fontSize: "14px", outline: "none",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(36,34,32,0.6)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md p-6"
        style={{ background: "#fff", borderRadius: "24px", border: "1.5px solid #242220", boxShadow: "8px 8px 0 #242220" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold mb-4" style={{ color: "#242220" }}>Új kurzus létrehozása</h3>

        {error && (
          <div className="p-3 mb-4 text-sm font-bold" style={{ background: "#FDECEA", border: "1.5px solid #D32F2F", borderRadius: "12px", color: "#D32F2F" }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-bold mb-1" style={{ color: "#242220" }}>Kurzus azonosító *</label>
            <input style={inputStyle} placeholder="pl. naturtrak-adatrogzites:auto" value={form.identifier}
              onChange={(e) => setForm({ ...form, identifier: e.target.value })} data-testid="new-course-identifier" />
            <p className="text-xs mt-1" style={{ color: "#242220", opacity: 0.4 }}>Egyedi azonosító – meg kell egyeznie a NaturTrak backend által küldött course_identifier-rel</p>
          </div>
          <div>
            <label className="block text-xs font-bold mb-1" style={{ color: "#242220" }}>Kurzus neve *</label>
            <input style={inputStyle} placeholder="pl. NaturTrak Adatrögzítés Mesterkurzus" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-course-name" />
          </div>
          <div>
            <label className="block text-xs font-bold mb-1" style={{ color: "#242220" }}>Rövid leírás</label>
            <textarea style={{ ...inputStyle, minHeight: 72, resize: "vertical" }} placeholder="Kurzus rövid bemutatása..."
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-bold mb-1" style={{ color: "#242220" }}>Borítókép URL</label>
            <input style={inputStyle} placeholder="https://..." value={form.cover_image}
              onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 font-bold text-sm"
              style={{ background: "transparent", borderRadius: "20px", border: "1.5px solid #242220", color: "#242220", cursor: "pointer" }}>
              Mégse
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 font-bold text-sm transition-all active:translate-x-[2px] active:translate-y-[2px]"
              style={{ background: "#FBD556", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220", color: "#242220", cursor: "pointer" }}
              data-testid="create-course-submit-btn">
              {loading ? "Létrehozás..." : "Kurzus létrehozása"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [cRes, sRes] = await Promise.all([adminListCourses(), adminGetStats()]);
        setCourses(cRes.data);
        setStats(sRes.data);
      } catch {
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const handleDelete = async (identifier) => {
    try {
      await adminDeleteCourse(identifier);
      setCourses((prev) => prev.filter((c) => c.identifier !== identifier));
      setDeleteConfirm(null);
    } catch (e) {
      alert("Törlés sikertelen: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: "#242220" }}>Admin Vezérlőpult</h1>
            <p className="text-sm" style={{ color: "#242220", opacity: 0.5 }}>Kurzusok és tartalmak kezelése</p>
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center gap-2 px-5 py-3 font-bold text-sm transition-all hover:translate-x-[1px] hover:translate-y-[1px] active:translate-x-[3px] active:translate-y-[3px]"
            style={{ background: "#FBD556", borderRadius: "30px", border: "1.5px solid #242220", boxShadow: "4px 4px 0 #242220", color: "#242220", cursor: "pointer" }}
            data-testid="new-course-btn"
          >
            <Plus size={16} strokeWidth={2.5} />
            Új kurzus
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <StatCard label="Összes kurzus" value={stats.total_courses} color="#FBD556" icon={BookOpen} />
            <StatCard label="Aktív tanulók" value={stats.total_users_with_progress} color="#A1E1B9" icon={Users} />
            <StatCard label="Teljesítések" value={stats.total_completions} color="#FF9456" icon={Award} />
          </div>
        )}

        <h2 className="text-lg font-bold mb-3" style={{ color: "#242220" }}>Kurzusok</h2>

        {loading && (
          <div className="text-center py-12" style={{ color: "#242220", opacity: 0.4 }}>
            <p className="font-bold">Betöltés...</p>
          </div>
        )}

        <div className="space-y-3">
          {courses.map((course) => (
            <div
              key={course.identifier}
              className="flex items-center gap-4 p-4"
              style={{
                background: "#fff",
                borderRadius: "16px",
                border: "1.5px solid #242220",
                boxShadow: "4px 4px 0 #242220",
              }}
              data-testid={`admin-course-row-${course.identifier}`}
            >
              {course.cover_image && (
                <img
                  src={course.cover_image}
                  alt={course.name}
                  className="w-16 h-12 object-cover flex-shrink-0"
                  style={{ borderRadius: "10px", border: "1.5px solid #242220" }}
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm" style={{ color: "#242220" }}>{course.name}</p>
                <p className="text-xs mt-0.5" style={{ color: "#242220", opacity: 0.5 }}>
                  {course.identifier} · {course.lessons?.length || 0} lecke · {course.final_exam?.questions?.length || 0} vizsgakérdés
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate(`/admin/courses/${encodeURIComponent(course.identifier)}`)}
                  className="flex items-center gap-1.5 px-3 py-2 font-bold text-xs transition-all active:translate-x-[1px] active:translate-y-[1px]"
                  style={{ background: "#A1E1B9", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "2px 2px 0 #242220", color: "#242220", cursor: "pointer" }}
                  data-testid={`edit-course-btn-${course.identifier}`}
                >
                  <Edit3 size={13} strokeWidth={2.5} />
                  Szerkesztés
                </button>
                <button
                  onClick={() => setDeleteConfirm(course.identifier)}
                  className="p-2"
                  style={{ background: "#FDECEA", borderRadius: "10px", border: "1.5px solid #D32F2F", color: "#D32F2F", cursor: "pointer" }}
                  data-testid={`delete-course-btn-${course.identifier}`}
                >
                  <Trash2 size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {!loading && courses.length === 0 && (
          <div
            className="text-center py-16"
            style={{ background: "#fff", borderRadius: "20px", border: "1.5px solid #242220" }}
          >
            <BookOpen size={40} strokeWidth={1.5} color="#242220" style={{ opacity: 0.2, margin: "0 auto 12px" }} />
            <p className="font-bold" style={{ color: "#242220" }}>Még nincsenek kurzusok</p>
            <p className="text-sm mt-1" style={{ color: "#242220", opacity: 0.4 }}>Hozd létre az első kurzust az "Új kurzus" gombbal.</p>
          </div>
        )}
      </div>

      {showNewModal && (
        <NewCourseModal
          onClose={() => setShowNewModal(false)}
          onCreated={(course) => {
            setCourses((prev) => [...prev, course]);
            setShowNewModal(false);
            navigate(`/admin/courses/${encodeURIComponent(course.identifier)}`);
          }}
        />
      )}

      {deleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(36,34,32,0.6)" }}
        >
          <div
            className="w-full max-w-sm p-6 text-center"
            style={{ background: "#fff", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "6px 6px 0 #242220" }}
          >
            <AlertCircle size={32} strokeWidth={2} color="#D32F2F" style={{ margin: "0 auto 12px" }} />
            <p className="font-bold text-lg mb-2" style={{ color: "#242220" }}>Biztos vagy benne?</p>
            <p className="text-sm mb-6" style={{ color: "#242220", opacity: 0.6 }}>
              A <strong>{deleteConfirm}</strong> kurzus és az összes tartalma törlésre kerül.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 font-bold text-sm"
                style={{ background: "transparent", borderRadius: "16px", border: "1.5px solid #242220", color: "#242220", cursor: "pointer" }}>
                Mégse
              </button>
              <button onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 font-bold text-sm"
                style={{ background: "#D32F2F", borderRadius: "16px", border: "1.5px solid #D32F2F", color: "#fff", cursor: "pointer" }}
                data-testid="confirm-delete-btn">
                Törlés
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
